import { Component, computed, effect, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IBriefingNote, IBriefingNoteFile } from '@pages/campaigns/campaigns.interface';
import {
  IBriefingNotesDialogData,
  IBriefingNotesDialogResult,
} from '@shared/components/briefing-notes-dialog/briefing-notes-dialog.interface';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { fileExtensionValidator, fileSizeValidator } from '@shared/validators/file-upload.validator';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { v4 as uuidv4 } from 'uuid';
import { IQuillEditor } from './briefing-notes.interface';

const MAX_TEXT_LENGTH = 10000;
const MAX_HTML_BYTES = 65535;

function getTextFromHtml(html: string | null): string {
  if (!html) {
    return '';
  }

  const el = document.createElement('div');
  el.innerHTML = html;

  return el.textContent ?? '';
}

function noteTextLengthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const text = getTextFromHtml(control.value as string | null);

    return text.length > MAX_TEXT_LENGTH ? { textTooLong: { actual: text.length, max: MAX_TEXT_LENGTH } } : null;
  };
}

function noteHtmlByteSizeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const html = control.value as string | null;

    if (!html) {
      return null;
    }

    const byteSize = new TextEncoder().encode(html).length;

    return byteSize > MAX_HTML_BYTES ? { htmlTooLarge: { actual: byteSize, max: MAX_HTML_BYTES } } : null;
  };
}

@Component({
  imports: [
    FileUploaderComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatMenuModule,
    QuillModule,
    ReactiveFormsModule,
    TranslateModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    IconComponent,
  ],
  selector: 'app-briefing-notes',
  styleUrl: './briefing-notes.component.scss',
  templateUrl: './briefing-notes.component.html',
})
export class BriefingNotesComponent {
  public readonly data = input.required<IBriefingNotesDialogData>();

  public readonly loading = input<boolean>(false);

  public readonly cancelled = output<void>();
  public readonly saved = output<IBriefingNotesDialogResult>();

  public readonly quillEditor = viewChild<QuillEditorComponent>('quillEditor');

  public readonly viewMode = computed<boolean>(() => this.data().viewMode);
  public readonly enableTextShowMore = computed<boolean>(() => this.data().enableTextShowMore ?? false);
  public readonly hideMessageSubtitle = computed<boolean>(() => this.data().hideMessageSubtitle ?? false);
  public readonly subtitleFontSize = computed<number>(() => this.data().subtitleFontSize ?? 16);
  public readonly subtitleFontWeight = computed<number>(() => this.data().subtitleFontWeight ?? 500);
  public readonly textLengthPercent = computed(() => (this.currentTextLength() / MAX_TEXT_LENGTH) * 100);

  public readonly noteId = signal<string>(uuidv4());
  public readonly files = signal<IBriefingNoteFile[]>([]);
  public readonly isNoteExpanded = signal<boolean>(false);
  public readonly hasOverflow = signal<boolean>(false);
  public readonly noteContentHeight = signal<number>(0);
  public readonly listUploaderSkeletonIndexes: readonly number[] = [0, 1, 2];
  public readonly currentTextLength = signal<number>(0);

  public readonly noteControl = new FormControl<string | null>(null, [
    noteTextLengthValidator(),
    noteHtmlByteSizeValidator(),
  ]);
  public readonly quillModules = {
    toolbar: '#briefingNotesToolbar',
  };

  public readonly maxTextLength = MAX_TEXT_LENGTH;
  public readonly collapsedHeight = 180;

  private readonly noteEditorWrapperRef = viewChild('noteEditorWrapper', { read: ElementRef });

  private readonly allowedFileExtensions = [
    'csv',
    'doc',
    'docx',
    'gif',
    'jpeg',
    'jpg',
    'webp',
    'pdf',
    'png',
    'ppt',
    'pptx',
    'txt',
    'xls',
    'xlsx',
  ] as const;
  private readonly maxFileSizeInBytes = 10000000;

  public readonly uploadFileControl = new FormControl<ICustomFile | null>(null, [
    fileExtensionValidator([...this.allowedFileExtensions]),
    fileSizeValidator(this.maxFileSizeInBytes),
  ]);

  private savedSelection: { index: number; length: number } | null = null;

  constructor() {
    effect(() => {
      const { briefingNote } = this.data();
      const isViewMode = this.viewMode();

      this.noteId.set(briefingNote?.id ?? uuidv4());
      this.noteControl.setValue(briefingNote?.note ?? null, { emitEvent: false });
      this.files.set([...(briefingNote?.files ?? [])]);
      this.currentTextLength.set(getTextFromHtml(briefingNote?.note ?? null).length);
      this.isNoteExpanded.set(false);

      if (isViewMode && this.enableTextShowMore()) {
        setTimeout(() => this.measureOverflow(), 0);
      } else {
        this.hasOverflow.set(false);
      }
    });

    this.noteControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((html) => {
      this.currentTextLength.set(getTextFromHtml(html).length);
    });
  }

  public onEditorCreated(): void {
    if (this.viewMode() && this.enableTextShowMore()) {
      setTimeout(() => this.measureOverflow(), 0);
    }
  }

  public toggleExpanded(): void {
    this.isNoteExpanded.update((prev) => !prev);
  }

  public onSaveClick(): void {
    if (this.noteControl.invalid) {
      this.noteControl.markAsTouched();

      return;
    }

    const briefingNote: IBriefingNote = {
      campaignId: this.data().campaignId,
      creatorId: this.data().creatorId,
      files: this.files(),
      id: this.noteId(),
      note: this.noteControl.value,
    };

    this.saved.emit({ briefingNote });
  }

  public onUploadSelected(event: { file: File; id: string }): void {
    const briefingNoteFile: IBriefingNoteFile = {
      fileId: event.id,
    };

    this.files.update((files) => [...files, briefingNoteFile]);
  }

  public removeFile(file: IBriefingNoteFile): void {
    this.removeFileFromState(file);
  }

  public rememberSelection(): void {
    const editor = this.getEditor();

    if (!editor) {
      return;
    }

    this.savedSelection = editor.getSelection(true);
  }

  public applyFormat(format: string, value: boolean | number | string): void {
    const editor = this.getEditor();

    if (!editor) {
      return;
    }

    this.restoreSelection(editor);

    if (typeof value === 'boolean') {
      const activeFormats = editor.getFormat();
      const isActive = !!activeFormats?.[format];
      editor.format(format, !isActive, 'user');
      this.savedSelection = editor.getSelection(true);

      return;
    }

    const activeFormats = editor.getFormat();
    const activeValue = activeFormats?.[format];
    const nextValue = activeValue === value ? false : value;

    editor.format(format, nextValue, 'user');
    this.savedSelection = editor.getSelection(true);
  }

  private measureOverflow(): void {
    const wrapperEl = this.noteEditorWrapperRef()?.nativeElement as HTMLDivElement | undefined;

    if (!wrapperEl) {
      return;
    }

    const fullHeight = wrapperEl.scrollHeight;

    this.noteContentHeight.set(fullHeight);
    this.hasOverflow.set(fullHeight > this.collapsedHeight);
  }

  private getEditor(): IQuillEditor | null {
    const editor = this.quillEditor()?.quillEditor;

    if (!editor) {
      return null;
    }

    return editor as unknown as IQuillEditor;
  }

  private restoreSelection(editor: IQuillEditor): void {
    if (!this.savedSelection) {
      return;
    }

    editor.setSelection(this.savedSelection.index, this.savedSelection.length, 'silent');
  }

  private removeFileFromState(removedFile: IBriefingNoteFile): void {
    this.files.update((files) =>
      files.filter((file) => {
        const isSamePersistentFile = !!removedFile.id && file.id === removedFile.id;
        const isSameTempFile = !removedFile.id && file.fileId === removedFile.fileId;

        if (isSamePersistentFile || isSameTempFile) {
          return false;
        }

        return true;
      }),
    );
  }
}
